import * as React from 'react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useLogin, useNotify, useRedirect } from 'react-admin';
import { makeStyles } from '@mui/styles';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowBack from '@mui/icons-material/ArrowBack';
import clsx from 'clsx';

import { adminHttp } from '@network';
import { axiosErrorToString } from '@utils';
import { ErrorMessage } from '@components';
import { useTimer } from './use-timer';
import { useSettingsContext } from '@providers';

interface ResendCountdownProps {
  resendCode: () => Promise<void>;
}

const ResendCountdown = ({ resendCode }: ResendCountdownProps) => {
  const { minutes, seconds, timeLeftMs, timerReset } = useTimer(60 * 1000);
  const cls = useStyles();

  const resend = async () => {
    if (timeLeftMs > 0) return;
    await resendCode();
    timerReset();
  };

  const disabled = timeLeftMs > 0;

  return (
    <Button variant="text"
      onClick={disabled ? undefined : resend}
      disabled={disabled}
      className={clsx(cls.buttonPhone, cls.buttonCountdown)}
    >
      <span>Send code again {disabled && `in ${minutes}:${seconds}`}</span>
    </Button>
  );
};

export const AppLoginPage = () => {
  const cls = useStyles();
  const redirect = useRedirect();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [type, setType] = useState<'email' | 'phone'>('email');
  const [hasCode, setHasCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const usernameRef = React.useRef<HTMLInputElement>(null);
  const codeRef = React.useRef<HTMLInputElement>(null);
  const { socket } = useSettingsContext();

  const login = useLogin();
  const notify = useNotify();

  useEffect(() => {
    const onAuthorizationEvent = async (adminId: number) => {
      console.log('[WEBSOCKET] on ADMIN_AUTHORIZATION_EVENT');
      const me = await adminHttp.me();
      if (me) {
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        localStorage.removeItem('redirectAfterLogin');

        redirect(redirectUrl || '/');
      }
    };

    socket.current && socket.current.on('ADMIN_AUTHORIZATION_EVENT', onAuthorizationEvent);

    return () => {
      socket.current && socket.current.off('ADMIN_AUTHORIZATION_EVENT', onAuthorizationEvent);
    };
  }, []);

  const setCodeHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCode(value);

    if (value.length === 6) {
      await login({ username, password, code: value, viaPhone: type === 'phone' });
    }
  };

  const handleSubmit = async (
    e?: FormEvent<HTMLFormElement>, sendToPhone?: boolean, resend?: boolean,
  ) => {
    if (e) {
      e.preventDefault();
    }
    let viaPhone = type === 'phone';

    if (!username || !password) {
      const errorMessage = 'Username and password are required';
      setError(errorMessage);
      notify(errorMessage);
      return;
    }

    if (sendToPhone) {
      viaPhone = true;
      setType('phone');
    }

    if (resend) {
      setHasCode(false);
    }

    if (!hasCode || sendToPhone || resend) {
      setError(null);
      setLoading(true);
      try {
        const { email, phone } = await adminHttp.getAuthCode({ username, password, viaPhone });
        setHasCode(true);
        notify(`Verification Code sent to ${viaPhone ? phone : email}`);
        setTimeout(() => void codeRef.current?.focus(), 100);
        setTimeout(() => void codeRef.current?.focus(), 300);
        setTimeout(() => void codeRef.current?.focus(), 600);
      } catch (err: any) {
        const errMessage = axiosErrorToString(err);
        setError(errMessage);
        notify(errMessage);
        setHasCode(false);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!code) {
      const errorMessage = 'Verification Code is required';
      setError(errorMessage);
      notify(errorMessage);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await login({ username, password, code, viaPhone });
    } catch (err: any) {
      const errMessage = axiosErrorToString(err);
      setError(errMessage);
      notify(errMessage);
    } finally {
      setLoading(false);
    }
  };

  const sendToPhone = () => void handleSubmit(undefined, true);
  const showPhoneButton = hasCode && !loading && type === 'email';

  const reset = () => {
    setUsername('');
    setPassword('');
    setCode('');
    setHasCode(false);
    setError(null);
    setType('email');
    setTimeout(() => void usernameRef.current?.focus(), 100);
    setTimeout(() => void usernameRef.current?.focus(), 300);
    setTimeout(() => void usernameRef.current?.focus(), 600);
  };

  const showResetButton = hasCode || error;
  const showResetCountdown = hasCode && !loading && !error;
  const resendCode = () => handleSubmit(undefined, undefined, true);

  return (
    <div className={cls.wrap}>
      <form onSubmit={(e) => handleSubmit(e)} className={cls.form}>
        <div className={cls.logo}>{Logo}</div>
        {showResetButton && (
          <IconButton onClick={reset} title="Reset form" className={cls.resetButton}>
            <ArrowBack />
          </IconButton>
        )}
        <input
          name="username"
          type="text"
          value={username}
          onChange={e => setUsername(e.target.value)}
          className={cls.input}
          placeholder="Username*"
          autoFocus
          ref={usernameRef}
          disabled={hasCode}
        />
        <input
          name="password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          className={cls.input}
          placeholder="Password*"
          disabled={hasCode}
        />
        <input
          name="code"
          type="text"
          value={code}
          onChange={setCodeHandler}
          className={clsx(cls.input, !hasCode && cls.blur)}
          placeholder="Verification Code*"
          disabled={!hasCode}
          ref={codeRef}
        />
        <ErrorMessage
          text={error || ''}
          className={clsx(cls.error, !error && cls.hidden)}
        />
        {showResetCountdown && <ResendCountdown resendCode={resendCode} />}
        <Button
          className={cls.button}
          variant="contained"
          type="submit"
          disabled={loading}
        >
          <span>Log In</span>
        </Button>
        <Button variant="text"
          onClick={sendToPhone}
          className={clsx(cls.buttonPhone, !showPhoneButton && cls.hidden)}
        >
          <span>Send Code Through SMS</span>
        </Button>
      </form>
    </div>
  );
};

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundImage: 'radial-gradient(#A2B3D3, #003086, #003086)',
  },
  error: {
    fontSize: 18,
    minHeight: 22,
  },
  hidden: {
    visibility: 'hidden',
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 10,
    paddingBottom: 60,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '462px',
    padding: '40px 40px 10px',
    backgroundColor: 'var(--foreground-color)',
    border: '1px solid var(--background-color)',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    position: 'relative',
  },
  resetButton: {
    position: 'absolute !important' as any,
    top: 20,
    left: 20,
    color: '#ffffff',
    backgroundColor: 'var(--primary-color)',
    '&:hover': {
      backgroundColor: 'var(--primary-color)',
      opacity: 0.85,
    },
  },
  input: {
    width: '100%',
    marginBottom: 20,
    borderRadius: 8,
    border: '1px solid var(--background-color)',
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25) inset',
    padding: '0px 16px 0px !important',
    lineHeight: '40px',
    fontSize: '18px',
    color: 'var(--text-color) !important',
    WebkitTextFillColor: 'var(--text-color)',
    backgroundColor: 'var(--background-color)',
    '&::placeholder': {
      color: 'var(--mutted-color) !important',
      WebkitTextFillColor: 'var(--mutted-color)',
      opacity: 1,
    },
    '&:-webkit-autofill': {
      WebkitTextFillColor: 'var(--text-color)',
      WebkitBoxShadow: '0 0 0 1000px var(--background-color) inset',
      caretColor: 'var(--text-color)',
    },
    '&:disabled': {
      backgroundColor: 'var(--background-color)',
      color: 'var(--mutted-color)',
      WebkitTextFillColor: 'var(--mutted-color)',
      cursor: 'not-allowed',
      opacity: 0.7,
    },
  },
  blur: {
    filter: 'blur(2px)',
  },
  button: {
    marginTop: '10px !important',
    backgroundColor: 'var(--primary-color) !important',
    height: '50px !important',
    width: '200px !important',
    borderRadius: '8px !important',
    color: '#ffffff !important',
    '& span': {
      textTransform: 'none',
      fontSize: '18px !important',
    },
  },
  buttonPhone: {
    marginTop: '10px !important',
    '& span': {
      textTransform: 'none',
      fontSize: '16px !important',
      color: 'var(--primary-color)',
      textDecoration: 'underline',
    },
  },
  buttonCountdown: {
    marginTop: '-40px !important',
    width: 200,
    display: 'flex',
    justifyContent: 'center',
    '&:disabled': {
      '& span': {
        color: 'var(--mutted-color) !important',
        textDecoration: 'none !important',
      },
    },
  },
});

const Logo = (
  <svg width="150"
    height="141"
    viewBox="0 0 150 141"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M51.2332 135.413L0 30.9246C4.11956 30.5902 8.15107 29.381 12.3118 29.1801C22.0129 28.7114 31.7839 32.034 37.1579 40.4209C41.9574 47.9113 45.1604 56.8978 49.0273 64.9283C55.1314 77.6053 62.086 89.8917 68.2011 102.568C68.6291 103.375 68.127 104.251 67.7789 105C64.0787 112.964 59.0426 121.842 54.7548 129.596C53.667 131.563 52.6063 133.628 51.2332 135.413Z"
      fill="#F48326" />
    <path
      d="M93.0102 74.3141C92.9522 74.0205 93.0482 73.8339 93.1469 73.5787C93.9627 71.4658 97.4583 66.5376 98.8767 64.3614C101.893 59.7336 105.1 54.9751 108.8 50.8701C112.399 46.8782 116.821 43.2914 120.375 39.284C122.703 36.6577 122.051 36.7593 119.203 38.109C110.886 42.0503 101.66 51.5088 96.0063 58.7396C92.4736 63.2581 89.607 68.2336 86.1709 72.818C84.5502 61.1759 88.5975 48.6682 95.6483 39.4279C103.397 29.2738 118.408 20.2771 129.19 13.2105C136.049 8.71465 143.145 4.56141 149.96 0C150.357 10.2639 147.718 20.1515 144.807 29.8673C141.298 41.575 136.045 57.6761 126.716 65.8432C117.504 73.9095 104.862 75.8365 93.0102 74.3141Z"
      fill="#73BA47" />
    <path
      d="M92.0545 75.8524C105.888 77.7038 120.611 75.2576 130.562 64.8393L103.835 117.962C98.9197 125.773 95.7348 138.154 85.359 140.085C79.6085 141.154 66.2582 141.202 60.2981 140.66C57.53 140.409 58.5718 138.999 59.3945 136.992C61.0696 132.905 63.7214 128.189 65.7737 124.186C74.1385 107.872 83.4291 92.0232 92.0545 75.8524Z"
      fill="var(--logo-color)" />
  </svg>
);
